import { z } from 'zod'

/**
 * Everything that can bill us, named once.
 *
 * The ledger column is text so adding a provider is not a migration, which
 * makes this list the only thing standing between a typo and a second series in
 * the admin chart that looks like a new vendor. Every write goes through
 * `costProviderSchema`, so a misspelling fails at the call site instead of
 * quietly splitting a month's spend in two.
 *
 * Unlike the tenant-facing reports, the admin area names its sources: knowing
 * which vendor a number came from *is* the task here.
 *
 * `openrouter` is the gateway every model call goes through, so it is one entry
 * however many labs answer behind it. Which model produced an answer is a
 * question about the run, and `ai_run` is where it is asked; a series per lab
 * here would be a chart of routing decisions rather than of what we are billed.
 */
export const COST_PROVIDERS = ['dataforseo', 'serper', 'anthropic', 'jina', 'openrouter'] as const

export type CostProvider = (typeof COST_PROVIDERS)[number]

export const costProviderSchema = z.enum(COST_PROVIDERS)

/** What the admin area calls each one. Separate from the id because the id is
 * stored in every row and renaming a vendor should not rewrite history. */
export const COST_PROVIDER_LABELS: Record<CostProvider, string> = {
  dataforseo: 'DataForSEO',
  serper: 'Serper',
  anthropic: 'Anthropic',
  jina: 'Jina',
  openrouter: 'OpenRouter',
}

/**
 * What a provider bills for, which is also the noun the interface counts in.
 * `request` is the fallback for anything metered per call.
 */
export const COST_UNITS = ['request', 'row', 'result', 'credit', 'token'] as const

export type CostUnit = (typeof COST_UNITS)[number]

/** Fixed slot per provider, so a provider keeps its colour as others come and
 * go — the chart's bands are keyed on this rather than on spend, because a
 * quiet month must not repaint the stack. */
export function costProviderSlot(provider: string): number {
  const index = (COST_PROVIDERS as readonly string[]).indexOf(provider)
  return index === -1 ? COST_PROVIDERS.length : index
}

export function costProviderLabel(provider: string): string {
  return COST_PROVIDER_LABELS[provider as CostProvider] ?? provider
}
